import { useEffect, useMemo, useState } from 'react';
import { FunnelInsightsApiClient } from '../api/insightsClient';

/**
 * analytics-versions-and-publish-flow (I4): the canvas report-mode version
 * switcher needs «which versions of this onboarding actually carried traffic
 * in the window» — the SAME rollup the Reports dashboard renders
 * (`aliasOnboardingSummaries`, one row per alias/onboarding/version).
 * NO new backend route (AC-028): windowed scopes reuse the dashboard read;
 * an all-time canvas first asks the coverage endpoint for the covered range.
 */

export interface TraffickedVersion {
  versionId: string;
  versionName?: string;
  starts: number;
}

interface UseTraffickedVersionsInput {
  projectId: string;
  onboardingId: string;
  enabled: boolean;
  /** Report window carried from the Open deep link (absent = all-time). */
  from?: string;
  to?: string;
}

interface UseTraffickedVersionsResult {
  versions: TraffickedVersion[];
  loading: boolean;
  /** true when the list could not be loaded — callers must degrade silently. */
  unavailable: boolean;
}

export function useTraffickedVersions(input: UseTraffickedVersionsInput): UseTraffickedVersionsResult {
  const [versions, setVersions] = useState<TraffickedVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const client = useMemo(() => new FunnelInsightsApiClient(), []);

  const canLoad = input.enabled && Boolean(input.projectId && input.onboardingId);

  useEffect(() => {
    if (!canLoad) {
      setVersions([]);
      setLoading(false);
      setUnavailable(false);
      return;
    }

    const abort = new AbortController();
    let cancelled = false;

    (async () => {
      setLoading(true);
      setUnavailable(false);
      try {
        let from = input.from;
        let to = input.to;
        if (!from || !to) {
          // All-time canvas: the dashboard read requires a window — span the
          // whole covered range reported by the coverage endpoint.
          const coverage = await client.getReportsCoverage({
            projectId: input.projectId,
            onboardingId: input.onboardingId,
            signal: abort.signal,
          });
          const minDate = coverage.data.minDate;
          const maxDate = coverage.data.maxDate;
          if (!minDate || !maxDate) {
            if (!cancelled) setVersions([]);
            return;
          }
          from = `${minDate}T00:00:00.000Z`;
          const endExclusive = new Date(Date.parse(`${maxDate}T00:00:00.000Z`) + 86_400_000);
          to = endExclusive.toISOString();
        }

        // W-R5 (DISC-AC-133/143): the versions rollup lives on the split
        // funnel endpoint — the monolith dashboard read is retired here too
        // (this hook only ever consumed aliasOnboardingSummaries).
        const funnelSection = await client.getReportsFunnelSection({
          projectId: input.projectId,
          onboardingId: input.onboardingId,
          from,
          to,
          signal: abort.signal,
        });

        // A version may be served by several aliases — aggregate per version.
        const byVersion = new Map<string, TraffickedVersion>();
        for (const row of funnelSection.data.aliasOnboardingSummaries ?? []) {
          if (row.onboardingId !== input.onboardingId || !row.versionId) continue;
          const existing = byVersion.get(row.versionId);
          if (existing) {
            existing.starts += row.startedJourneys || 0;
            if (!existing.versionName && row.identity?.versionName) {
              existing.versionName = row.identity.versionName;
            }
          } else {
            byVersion.set(row.versionId, {
              versionId: row.versionId,
              versionName: row.identity?.versionName,
              starts: row.startedJourneys || 0,
            });
          }
        }
        if (!cancelled) {
          setVersions(Array.from(byVersion.values()).sort((a, b) => b.starts - a.starts));
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[useTraffickedVersions] version list unavailable:', error);
          setVersions([]);
          setUnavailable(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [canLoad, client, input.from, input.onboardingId, input.projectId, input.to]);

  return { versions, loading, unavailable };
}
